import { baseApi } from './baseApi';
import {
  Recipe,
  RecipeCoverage,
  ParsedIngredients,
  CreateRecipeRequest,
  UpdateRecipeRequest,
  RecipeCost,
  VariantRecipeConfig,
  UpdateVariantRecipeConfigRequest,
  SimulatedCost,
  Preparation,
  SavePreparationRequest,
} from '../types/inventory';

export const recipesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getRecipes: builder.query<Recipe[], void>({
      query: () => '/recipes',
      providesTags: ['Recipe'],
    }),
    // InventoryItem también: un vínculo directo producto↔insumo cuenta como cubierto
    getRecipeCoverage: builder.query<RecipeCoverage, number | void>({
      query: (days) => `/recipes/coverage?days=${days ?? 90}`,
      providesTags: ['Recipe', 'InventoryItem'],
    }),
    getRecipe: builder.query<Recipe, string>({
      query: (id) => `/recipes/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Recipe', id }],
    }),
    // Lectura sin efectos: no crea insumos ni recetas, sólo devuelve los renglones
    // sugeridos, así que no invalida ningún tag
    parseIngredientText: builder.mutation<ParsedIngredients, string>({
      query: (text) => ({ url: '/recipes/parse-text', method: 'POST', body: { text } }),
    }),
    createRecipe: builder.mutation<Recipe, CreateRecipeRequest>({
      query: (data) => ({ url: '/recipes', method: 'POST', body: data }),
      invalidatesTags: ['Recipe'],
    }),
    updateRecipe: builder.mutation<Recipe, { id: string; data: UpdateRecipeRequest }>({
      query: ({ id, data }) => ({ url: `/recipes/${id}`, method: 'PUT', body: data }),
      invalidatesTags: ['Recipe'],
    }),
    deleteRecipe: builder.mutation<void, string>({
      query: (id) => ({ url: `/recipes/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Recipe'],
    }),
    getRecipeCost: builder.query<RecipeCost, string>({
      query: (id) => `/recipes/${id}/cost`,
      providesTags: (_result, _error, id) => [{ type: 'Recipe', id }],
    }),
    // POST porque manda la selección, pero se cachea como query por combinación
    simulateRecipeCost: builder.query<SimulatedCost, { productId: string; variantIds: string[] }>({
      query: ({ productId, variantIds }) => ({
        url: `/recipes/product/${productId}/cost-simulation`,
        method: 'POST',
        body: { variantIds },
      }),
      providesTags: (_result, _error, { productId }) => [{ type: 'Recipe', id: `sim-${productId}` }],
    }),
    getVariantRecipeConfig: builder.query<VariantRecipeConfig, string>({
      query: (productId) => `/recipes/product/${productId}/variant-config`,
      providesTags: (_result, _error, productId) => [{ type: 'Recipe', id: `variant-config-${productId}` }],
    }),
    updateVariantRecipeConfig: builder.mutation<
      VariantRecipeConfig,
      { productId: string; data: UpdateVariantRecipeConfigRequest }
    >({
      query: ({ productId, data }) => ({
        url: `/recipes/product/${productId}/variant-config`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Recipe'],
    }),
    // InventoryItem también: su costo sale del de los insumos, y una preparación es un insumo
    // que aparece en los selectores de ingredientes. Recipe porque cambia el costo de los
    // platillos que la usan.
    getPreparations: builder.query<Preparation[], void>({
      query: () => '/recipes/preparations',
      providesTags: ['Recipe', 'InventoryItem'],
    }),
    createPreparation: builder.mutation<Preparation, SavePreparationRequest>({
      query: (data) => ({ url: '/recipes/preparations', method: 'POST', body: data }),
      invalidatesTags: ['Recipe', 'InventoryItem', 'InventoryCategory'],
    }),
    updatePreparation: builder.mutation<Preparation, { id: string; data: SavePreparationRequest }>({
      query: ({ id, data }) => ({ url: `/recipes/preparations/${id}`, method: 'PUT', body: data }),
      invalidatesTags: ['Recipe', 'InventoryItem', 'InventoryCategory'],
    }),
    deletePreparation: builder.mutation<void, string>({
      query: (id) => ({ url: `/recipes/preparations/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Recipe', 'InventoryItem', 'InventoryCategory'],
    }),
  }),
});

export const {
  useGetRecipesQuery,
  useGetRecipeCoverageQuery,
  useParseIngredientTextMutation,
  useGetRecipeQuery,
  useCreateRecipeMutation,
  useUpdateRecipeMutation,
  useDeleteRecipeMutation,
  useGetRecipeCostQuery,
  useSimulateRecipeCostQuery,
  useGetVariantRecipeConfigQuery,
  useUpdateVariantRecipeConfigMutation,
  useGetPreparationsQuery,
  useCreatePreparationMutation,
  useUpdatePreparationMutation,
  useDeletePreparationMutation,
} = recipesApi;
